import { cn } from '@/lib/utils';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  Input,
  Label,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Anchor,
  Bird,
  Compass,
  Flag,
  Flame,
  Heart,
  type LucideIcon,
  Map,
  Mountain,
  Rocket,
  Shield,
  Ship,
  Star,
  Sun,
  Target,
  Trophy,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';

// Available icons for alliances (reuses team icon set)
const ALLIANCE_ICONS: { name: string; icon: LucideIcon }[] = [
  { name: 'shield', icon: Shield },
  { name: 'flag', icon: Flag },
  { name: 'trophy', icon: Trophy },
  { name: 'star', icon: Star },
  { name: 'rocket', icon: Rocket },
  { name: 'flame', icon: Flame },
  { name: 'zap', icon: Zap },
  { name: 'target', icon: Target },
  { name: 'compass', icon: Compass },
  { name: 'map', icon: Map },
  { name: 'mountain', icon: Mountain },
  { name: 'sun', icon: Sun },
  { name: 'anchor', icon: Anchor },
  { name: 'ship', icon: Ship },
  { name: 'bird', icon: Bird },
  { name: 'heart', icon: Heart },
  { name: 'users', icon: Users },
];

// Available colors for alliances
const ALLIANCE_COLORS = [
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Emerald', value: '#10B981' },
];

interface Team {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
  allianceId?: string | null;
}

interface Alliance {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
  color?: string | null;
  teams: Array<{ id: string; name: string; iconName: string; iconColor: string }>;
}

interface AllianceEditDialogProps {
  eventId: string;
  alliance?: Alliance | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for creating or editing an alliance
 */
export function AllianceEditDialog({ eventId, alliance, open, onOpenChange }: AllianceEditDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const isEditing = !!alliance;

  const [name, setName] = useState(alliance?.name || '');
  const [iconName, setIconName] = useState(alliance?.iconName || 'shield');
  const [iconColor, setIconColor] = useState(alliance?.iconColor || '#6366F1');
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(
    new Set(alliance?.teams.map((t) => t.id) || []),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when dialog opens with new alliance
  useEffect(() => {
    if (open) {
      setName(alliance?.name || '');
      setIconName(alliance?.iconName || 'shield');
      setIconColor(alliance?.iconColor || '#6366F1');
      setSelectedTeamIds(new Set(alliance?.teams.map((t) => t.id) || []));
      setErrors({});
    }
  }, [open, alliance]);

  // Fetch all teams for this event
  const { data: teamsData } = useQuery<{ success: boolean; data: Team[] }>({
    queryKey: ['EventTeams', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams`),
    enabled: open && !!eventId,
  });

  const teams = teamsData?.data || [];

  // Filter to show unassigned teams + teams already in this alliance
  const availableTeams = teams.filter(
    (t) => !t.allianceId || t.allianceId === alliance?.id,
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing) {
        // Update alliance
        await apiClient.patch(`/events/${eventId}/alliances/${alliance.id}`, {
          name: name.trim(),
          iconName,
          iconColor,
        });

        // Sync team assignments
        const currentTeamIds = new Set(alliance.teams.map((t) => t.id));

        // Add new teams and remove old ones in parallel
        const addPromises = [...selectedTeamIds]
          .filter((teamId) => !currentTeamIds.has(teamId))
          .map((teamId) =>
            apiClient.post(`/events/${eventId}/alliances/${alliance.id}/teams`, { teamId }),
          );
        const removePromises = [...currentTeamIds]
          .filter((teamId) => !selectedTeamIds.has(teamId))
          .map((teamId) =>
            apiClient.delete(`/events/${eventId}/alliances/${alliance.id}/teams/${teamId}`),
          );
        await Promise.all([...addPromises, ...removePromises]);
      } else {
        // Create alliance
        const result = await apiClient.post(`/events/${eventId}/alliances`, {
          name: name.trim(),
          iconName,
          iconColor,
        }) as { data: { id: string } };

        // Assign teams in parallel
        await Promise.all(
          [...selectedTeamIds].map((teamId) =>
            apiClient.post(`/events/${eventId}/alliances/${result.data.id}/teams`, { teamId }),
          ),
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['EventAlliances', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      toast.success(isEditing ? 'Alliance updated' : 'Alliance created');
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save alliance');
    },
  });

  /** Validate the form */
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) {
      errs.name = 'Alliance name is required';
    } else if (name.trim().length > 100) {
      errs.name = 'Name must be 100 characters or less';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /** Handle form submission */
  const handleSubmit = () => {
    if (!validate()) return;
    saveMutation.mutate();
  };

  /** Toggle a team selection */
  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const CurrentIcon = ALLIANCE_ICONS.find((i) => i.name === iconName)?.icon || Shield;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Alliance' : 'Create Alliance'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the alliance details and team assignments.' : 'Create a new alliance and assign teams.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-4">
          {/* Preview */}
          <div className="flex items-center justify-center">
            <div
              className="flex items-center justify-center h-16 w-16 rounded-full"
              style={{ backgroundColor: `${iconColor}20` }}
            >
              <CurrentIcon className="h-8 w-8" style={{ color: iconColor }} />
            </div>
          </div>

          <FormFieldGroup name="name" label="Alliance Name" required error={errors.name}>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter alliance name"
              className={cn(errors.name && 'border-destructive')}
            />
          </FormFieldGroup>

          {/* Icon Picker */}
          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="grid grid-cols-6 gap-2">
              {ALLIANCE_ICONS.map((item) => {
                const Icon = item.icon;
                const isSelected = item.name === iconName;
                return (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => setIconName(item.name)}
                    className={cn(
                      'flex items-center justify-center h-10 w-10 rounded-lg border transition-all',
                      isSelected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1'
                        : 'border-muted hover:border-primary/50 hover:bg-muted/50',
                    )}
                  >
                    <Icon
                      className="h-5 w-5"
                      style={{ color: isSelected ? iconColor : undefined }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {ALLIANCE_COLORS.map((color) => {
                const isSelected = color.value === iconColor;
                return (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setIconColor(color.value)}
                    className={cn(
                      'h-8 w-8 rounded-full transition-all',
                      isSelected && 'ring-2 ring-offset-2 ring-primary',
                    )}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                );
              })}
            </div>
          </div>

          {/* Team Assignment */}
          <div className="space-y-2">
            <Label>Teams</Label>
            <p className="text-sm text-muted-foreground">Select teams to include in this alliance</p>
            {availableTeams.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-2">No available teams</p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2">
                {availableTeams.map((team) => {
                  const isSelected = selectedTeamIds.has(team.id);
                  return (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => toggleTeam(team.id)}
                      className={cn(
                        'flex items-center gap-3 w-full px-3 py-2 rounded-md text-left transition-colors',
                        isSelected
                          ? 'bg-primary/10 border border-primary/30'
                          : 'hover:bg-muted/50',
                      )}
                    >
                      <div
                        className="flex items-center justify-center h-7 w-7 rounded-full shrink-0"
                        style={{ backgroundColor: `${team.iconColor}20` }}
                      >
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: team.iconColor }}
                        />
                      </div>
                      <span className="text-sm font-medium flex-1">{team.name}</span>
                      <div
                        className={cn(
                          'h-4 w-4 rounded border transition-colors',
                          isSelected
                            ? 'bg-primary border-primary'
                            : 'border-muted-foreground/30',
                        )}
                      >
                        {isSelected && (
                          <svg viewBox="0 0 16 16" fill="white" className="h-4 w-4">
                            <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={saveMutation.isPending}>
            {isEditing ? 'Save Changes' : 'Create Alliance'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
